import { useState } from 'react';
import type { LabTest, UnresolvedTestName } from '#shared/types';
import { Banner } from '#client/components/ui/Banner';
import { Button } from '#client/components/ui/Button';
import { Card } from '#client/components/ui/Card';
import { Select } from '#client/components/ui/Select';
import { useMutation } from '#client/hooks/useMutation';
import { RESOURCE } from '#client/lib/routes';
import { nameAndCodeOptions } from '#client/lib/form-choices';
import { countOf } from '#client/lib/format';
import { createTestAlias } from './import.service';
import { useAllRecords } from '#client/hooks/useAllRecords';

/**
 * Names in the sheet that match no test, with a way to fix them.
 *
 * The highest-value screen in the importer, and the reason `LabTestAlias`
 * exists: an operator resolves "L.F.T" once and every later import of every
 * later file resolves it automatically. Without this, a lab that re-imports its
 * history quarterly makes the same forty decisions every quarter.
 *
 * Ranked by how many rows each name blocks, so the decision that fixes forty
 * rows is offered before the one that fixes one. The suggestions come from a
 * similarity score, and they are only ever *suggestions* — nothing is matched
 * automatically on closeness, because "Vitamin D" and "Vitamin B12" share most
 * of their letters and are not the same test.
 */
export function AliasResolver({
  unresolved,
  onResolved,
}: {
  unresolved: readonly UnresolvedTestName[];
  onResolved: () => void;
}) {
  const tests = useAllRecords<LabTest>({ path: RESOURCE.labTests });
  const aliasRequest = useMutation('That match was not saved.');
  const [savingText, setSavingText] = useState<string | null>(null);
  const [choice, setChoice] = useState<Record<string, string>>({});

  if (unresolved.length === 0) return null;

  async function handleResolve(sourceText: string) {
    const testId = choice[sourceText];
    if (!testId) return;

    setSavingText(sourceText);
    const outcome = await aliasRequest.run(() =>
      createTestAlias({ source_text: sourceText, test_id: Number(testId) }),
    );
    setSavingText(null);
    // Re-checking is what makes the fix visible: the alias now resolves for
    // every row that mentioned this name, not just the one being looked at.
    if (outcome.ok) onResolved();
  }

  const total = unresolved.reduce((sum, entry) => sum + entry.row_count, 0);

  return (
    <Card
      title="Names this does not recognise"
      subtitle={`${unresolved.length} names across ${total} rows. Matching one here teaches it for good.`}
    >
      {aliasRequest.errorMessage && <Banner tone="critical">{aliasRequest.errorMessage}</Banner>}
      <ul className="c-alias-list">
        {unresolved.map((entry) => (
          <AliasRow
            key={entry.source_text}
            entry={entry}
            options={testOptionsFor(entry, tests.items)}
            chosenTestId={choice[entry.source_text] ?? ''}
            onChoose={(testId) =>
              setChoice((current) => ({ ...current, [entry.source_text]: testId }))
            }
            isSaving={savingText === entry.source_text}
            isLocked={savingText !== null}
            onMatch={() => void handleResolve(entry.source_text)}
          />
        ))}
      </ul>

      <p className="c-field__help">
        Rows with an unmatched name still import — the visit is recorded without that test rather
        than being thrown away.
      </p>
    </Card>
  );
}

/**
 * Suggestions first and marked as such, then the whole catalogue — the ranking
 * is a shortcut, not a filter, and a list that hid the right answer would be
 * worse than no list.
 */
function testOptionsFor(
  entry: UnresolvedTestName,
  catalogue: readonly LabTest[],
): { value: string; label: string }[] {
  const suggested = new Set(entry.suggestions.map((hint) => hint.id));
  return [
    { value: '', label: 'Leave unmatched' },
    ...entry.suggestions.map((test) => ({
      value: String(test.id),
      label: `${test.name} (${test.code}) — closest match`,
    })),
    ...nameAndCodeOptions(catalogue.filter((test) => !suggested.has(test.id))),
  ];
}

interface AliasRowProps {
  entry: UnresolvedTestName;
  options: { value: string; label: string }[];
  chosenTestId: string;
  onChoose: (testId: string) => void;
  isSaving: boolean;
  /** Another row's match is being saved. */
  isLocked: boolean;
  onMatch: () => void;
}

function AliasRow({
  entry,
  options,
  chosenTestId,
  onChoose,
  isSaving,
  isLocked,
  onMatch,
}: AliasRowProps) {
  return (
    <li className="c-alias">
      <div className="c-alias__name">
        <strong>{entry.source_text}</strong>
        <span className="c-alias__count">{countOf(entry.row_count, 'row')}</span>
      </div>

      <Select
        label={`Which test is "${entry.source_text}"?`}
        hideLabel
        options={options}
        value={chosenTestId}
        onChange={onChoose}
      />

      <Button
        size="sm"
        variant="primary"
        disabled={!chosenTestId || isLocked}
        loading={isSaving}
        onClick={onMatch}
      >
        Match
      </Button>
    </li>
  );
}
